import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type Unsubscribe
} from "firebase/firestore";
import type { User } from "firebase/auth";
import type { LabelElement, LabelTemplate } from "../types/imobi";
import { db } from "./firebase";

function normalizeTemplateId(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function numberValue(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function booleanValue(value: unknown, fallback: boolean) {
  return typeof value === "boolean" ? value : fallback;
}

function mapMargins(data: Record<string, unknown>, fallback: number) {
  const margins = data.marginsMm && typeof data.marginsMm === "object" ? data.marginsMm as Record<string, unknown> : {};
  return {
    top: numberValue(margins.top, fallback),
    right: numberValue(margins.right, fallback),
    bottom: numberValue(margins.bottom, fallback),
    left: numberValue(margins.left, fallback)
  };
}

function mapElements(value: unknown): LabelElement[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item, index) => ({
      id: String(item.id ?? `element-${index}`),
      type: item.type as LabelElement["type"],
      field: item.field as LabelElement["field"],
      xMm: numberValue(item.xMm, 0),
      yMm: numberValue(item.yMm, 0),
      widthMm: numberValue(item.widthMm, 20),
      heightMm: numberValue(item.heightMm, 8),
      fontSize: typeof item.fontSize === "number" ? item.fontSize : undefined,
      color: typeof item.color === "string" ? item.color : undefined,
      fontWeight: item.fontWeight as LabelElement["fontWeight"],
      textAlign: item.textAlign as LabelElement["textAlign"],
      showQrCode: typeof item.showQrCode === "boolean" ? item.showQrCode : undefined,
      showBarcode: typeof item.showBarcode === "boolean" ? item.showBarcode : undefined,
      showNumber: typeof item.showNumber === "boolean" ? item.showNumber : undefined,
      codeSource: item.codeSource as LabelElement["codeSource"]
    }));
}

function mapTemplate(id: string, data: Record<string, unknown>): LabelTemplate {
  const marginMm = numberValue(data.marginMm, 2);
  const showQrCode = booleanValue(data.showQrCode, true);
  const showBarcode = booleanValue(data.showBarcode, false);

  return {
    id,
    name: String(data.name ?? id),
    widthMm: numberValue(data.widthMm, 50),
    heightMm: numberValue(data.heightMm, 30),
    marginMm,
    marginsMm: mapMargins(data, marginMm),
    showCompanyLogo: booleanValue(data.showCompanyLogo, true),
    showImobiLogo: booleanValue(data.showImobiLogo, true),
    showQrCode,
    showBarcode,
    codeMode: data.codeMode as LabelTemplate["codeMode"] ?? (showQrCode && showBarcode ? "both" : showQrCode ? "qr" : showBarcode ? "barcode" : "none"),
    codeSource: data.codeSource as LabelTemplate["codeSource"] ?? "assetUrl",
    visibleFields: Array.isArray(data.visibleFields) ? data.visibleFields as LabelTemplate["visibleFields"] : ["patrimonyNumber", "shortDescription"],
    fontSize: numberValue(data.fontSize, 8),
    fontFamily: data.fontFamily as LabelTemplate["fontFamily"] ?? "Arial",
    fieldStyles: typeof data.fieldStyles === "object" && data.fieldStyles ? data.fieldStyles as LabelTemplate["fieldStyles"] : {},
    elements: mapElements(data.elements),
    printDensity: data.printDensity as LabelTemplate["printDensity"] ?? "normal",
    active: booleanValue(data.active, true),
    createdAt: String(data.createdAt ?? ""),
    createdBy: String(data.createdBy ?? "system"),
    updatedAt: data.updatedAt ? String(data.updatedAt) : undefined,
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeGlobalLabelTemplates(onChange: (items: LabelTemplate[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "labelTemplates"), orderBy("name")),
    (snapshot) => onChange(snapshot.docs.map((itemDoc) => mapTemplate(itemDoc.id, itemDoc.data()))),
    (error) => onError(error.message)
  );
}

function stripUndefined<T extends Record<string, unknown>>(value: T): T {
  const cleaned = { ...value };
  Object.keys(cleaned).forEach((key) => {
    if (cleaned[key] === undefined) {
      delete cleaned[key];
    }
  });
  return cleaned;
}

export async function saveGlobalLabelTemplate(template: LabelTemplate, user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const id = template.id || normalizeTemplateId(template.name) || doc(collection(db, "labelTemplates")).id;
  const marginMm = Math.min(template.marginsMm?.top ?? template.marginMm, template.marginsMm?.right ?? template.marginMm, template.marginsMm?.bottom ?? template.marginMm, template.marginsMm?.left ?? template.marginMm);
  const elements = template.elements?.map((element) => stripUndefined({ ...element }));

  await setDoc(doc(db, "labelTemplates", id), {
    ...template,
    id,
    companyId: null,
    marginMm,
    elements: elements ?? [],
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    createdAt: template.createdAt || serverTimestamp(),
    createdBy: template.createdBy || user.uid
  }, { merge: true });

  return id;
}
